import * as React from "react";
import {LogView} from "../../logView/LogView";
import {AiAgentTaskIterationTable} from "./AiAgentTaskIterationTable";
import {Button, Icon, Message, Modal, Tab} from "semantic-ui-react";
import AceEditor from "react-ace";
import {DocumentationEditor} from "./DocumentationEditor";
import {AiAgentTaskIterationConversationResultFiles} from "./AiAgentTaskIterationConversationResultFiles";
import {aiAgentTasks} from "./AiScripterPage";
import { AIAgentTask } from "../../../../../generated";
import {ConfigContext} from "../../../context/ConfigContext";
import {clampTabIndex} from "../../../context/TabSession";

export interface AiScripterResultTabViewProps {
    xRequestId: string,
    aiAgentTaskId: String | null,
}

export function AiScripterResultTabView(props: React.PropsWithChildren<AiScripterResultTabViewProps>) {
    const context = React.useContext(ConfigContext);
    const [isFullScreen, setIsFullScreen] = React.useState<boolean>(false);
    const [isDarkTheme, setIsDarkTheme] = React.useState<boolean>(true);
    const [aiAgentTask, setAiAgentTask] = React.useState<AIAgentTask | null>(null);

    React.useEffect(() => {
        setAiAgentTask(aiAgentTasks.value.find(task => task._id === props.aiAgentTaskId) || null)
    }, [aiAgentTasks.value, props.aiAgentTaskId]);

    function logView() {
        function cleanXRequestId(xRequestId: string): string {
            const match = xRequestId?.match(/\b[0-9a-fA-F-]{36}\b/);
            return match ? match[0] : "";
        }

        function concatXreqIds() {
            let ids = []
            if (props.xRequestId) {
                ids.push(props.xRequestId)
            }
            let task = aiAgentTask
            if (task) {
                ids.push(cleanXRequestId(task.xrequestId))
                task.iterations?.forEach(iteration => {
                    if (iteration.xRequestId) {
                        ids.push(cleanXRequestId(iteration.xRequestId))
                    }
                })
            }

            let uniqueIds = [...new Set(ids)].filter(id => id !== "" && id !== undefined);
            return uniqueIds.join(" OR ")
        }

        let query = concatXreqIds()
        return <LogView
            key={query}
            query={query}
            origin={"AiScripterPage"}
            initialTimespan={60 * 60} // 1 hour
            responseLogs={null}
        />
    }


    function iterations() {
        return <AiAgentTaskIterationTable
            aiAgentTask={aiAgentTask}
        />
    }


    function copyScriptToClipboard() {
        let task = aiAgentTask

        if (task?.latestUpdatedScript) {
            navigator.clipboard.writeText(task.latestUpdatedScript)
                .then(() => {
                    console.log('Script copied to clipboard');
                })
                .catch(err => {
                    console.error('Failed to copy script: ', err);
                });
        }
    }

    function toggleFullScreen() {
        setIsFullScreen(!isFullScreen);
    }

    function toggleTheme() {
        setIsDarkTheme(!isDarkTheme);
    }

    function latestUpdatedScript() {
        const currentTheme = isDarkTheme ? "monokai" : "github";
        let task = aiAgentTask

        return (
            <>
                <div style={{display: "flex", justifyContent: "flex-end", gap: "2px", marginBottom: "10px"}}>
                    {task?.latestUpdatedScript && (
                        <>
                            <Button
                                size="small"
                                icon
                                onClick={toggleTheme}
                                title={isDarkTheme ? "Switch to light theme" : "Switch to dark theme"}
                            >
                                <Icon name={isDarkTheme ? "sun" : "moon"}/>
                            </Button>
                            <Button
                                size="small"
                                icon
                                onClick={copyScriptToClipboard}
                                title="Copy script to clipboard"
                            >
                                <Icon name="copy"/>
                            </Button>
                            <Button
                                size="small"
                                icon
                                onClick={toggleFullScreen}
                                title="Expand view"
                            >
                                <Icon name="expand"/>
                            </Button>
                        </>
                    )}
                </div>

                {task?.latestUpdatedScript ? (
                    <AceEditor
                        theme={currentTheme}
                        mode="groovy"
                        value={task.latestUpdatedScript}
                        placeholder='The latest updated script will be shown here'
                        width="100%"
                        height="100%"
                        readOnly={true}
                        setOptions={{useWorker: false}}
                    />
                ) : (
                    <Message style={{color: 'black'}}>
                        <Message.Header>No Conversion Result Available</Message.Header>
                        <p>No script conversion result is available for the current task.</p>
                    </Message>
                )}

                <Modal
                    open={isFullScreen}
                    onClose={toggleFullScreen}
                    style={{width: '90%', margin: 'auto'}}
                >
                    <Modal.Header>
                        <div style={{display: "flex", justifyContent: "space-between"}}>
                            <span>Latest Updated Script</span>
                            <div>
                                <Button
                                    size="tiny"
                                    icon
                                    onClick={toggleTheme}
                                    title={isDarkTheme ? "Switch to light theme" : "Switch to dark theme"}
                                >
                                    <Icon name={isDarkTheme ? "sun" : "moon"}/>
                                </Button>
                                <Button
                                    size="tiny"
                                    icon
                                    onClick={copyScriptToClipboard}
                                >
                                    <Icon name="copy"/>
                                </Button>
                                <Button
                                    size="tiny"
                                    icon
                                    onClick={toggleFullScreen}
                                >
                                    <Icon name="compress"/>
                                </Button>
                            </div>
                        </div>
                    </Modal.Header>
                    <Modal.Content style={{height: '80vh', padding: '0'}}>
                        {task?.latestUpdatedScript && (
                            <AceEditor
                                theme={currentTheme}
                                mode="groovy"
                                value={task.latestUpdatedScript}
                                placeholder='The latest updated script will be shown here'
                                width="100%"
                                height="100%"
                                fontSize={16}
                                readOnly={true}
                                setOptions={{
                                    useWorker: false,
                                    showPrintMargin: false,
                                    highlightActiveLine: true,
                                    showGutter: true
                                }}
                            />
                        )}
                    </Modal.Content>
                </Modal>
            </>
        );
    }


    function conversionResultFiles() {
        return <AiAgentTaskIterationConversationResultFiles
            key={"conversionResultFiles_"+props.aiAgentTaskId}
            aiAgentTask={aiAgentTask}
        />
    }

    function documentation() {
        return <DocumentationEditor/>;
    }

    React.useEffect(() => {
        const iterationsWithConv = aiAgentTask?.iterations?.filter(
            it => it.queuedEventConversionResponse?.resultFiles?.length > 0
        ) || [];
        /*
                if (iterationsWithConv.length > 0) {
                    // Find the iteration with the most recent timestamp.
                    let latestIterationWithConv = iterationsWithConv[0];
                    let maxTimestamp = iterationsWithConv[0].timestamp ? new Date(iterationsWithConv[0].timest).getTime() : 0;
                    /*
                                for (let i = 1; i < iterationsWithConv.length; i++) {
                                    const currentIter = iterationsWithConv[i];
                                    // Handle potentially undefined or invalid timestamps gracefully
                                    const currentTimestampNum = currentIter.timestamp ? new Date(currentIter.timestamp).getTime() : 0;
                                    // Ensure comparison is valid if timestamps are NaN (e.g. from invalid date string)
                                    if (!isNaN(currentTimestampNum) && (isNaN(maxTimestamp) || currentTimestampNum > maxTimestamp)) {
                                        latestIterationWithConv = currentIter;
                                        maxTimestamp = currentTimestampNum;
                                    } else if (isNaN(maxTimestamp) && !isNaN(currentTimestampNum)) { // if maxTimestamp was NaN but current is not
                                        latestIterationWithConv = currentIter;
                                        maxTimestamp = currentTimestampNum;
                                    }
                                }

                                // Find the index of this latest iteration within the filtered 'iterationsWithConv' array.
                                const latestIterArrayIndex = iterationsWithConv.findIndex(it => it === latestIterationWithConv);

                               if (latestIterArrayIndex === -1) {
                                    // This should ideally not happen if latestIterationWithConv came from iterationsWithConv
                                    console.error("Error: Latest iteration with conversion not found in filtered list.");
                                    setSelectedIterationIndex(null);
                                    setSelectedFileOptionValue(null);
                                    setSelectedConversionFile(null);
                                    return;
                                }

                                setSelectedIterationIndex(latestIterArrayIndex);

                                const latestIterationData = latestIterationWithConv; // This is iterationsWithConv[latestIterArrayIndex]
                                const filesInLatest = latestIterationData?.queuedEventConversionResponse?.resultFiles;

                                if (filesInLatest && filesInLatest.length > 0) {
                                    const firstFileIndexInOptions = 0;
                                    setSelectedFileOptionValue(firstFileIndexInOptions);
                                    processAndSetFile(filesInLatest[firstFileIndexInOptions], firstFileIndexInOptions);
                                } else {
                                    setSelectedFileOptionValue(null);
                                    setSelectedConversionFile(null);
                                }
                            } else {
                                // No iterations with conversion results found
                                setSelectedIterationIndex(null);
                                setSelectedFileOptionValue(null);
                                setSelectedConversionFile(null);
                            }*/
    }, [props.aiAgentTaskId]);


    const panes = [
        {
            menuItem: "Iterations",
            render: () => <Tab.Pane>{iterations()}</Tab.Pane>,
        },
        {
            menuItem: "Log",
            render: () => <Tab.Pane>{logView()}</Tab.Pane>,
        },
        {
            menuItem: "Latest Updated Script",
            render: () => <Tab.Pane>{latestUpdatedScript()}</Tab.Pane>,
        },
        {
            menuItem: "Conversion Result Files",
            render: () => <Tab.Pane>{conversionResultFiles()}</Tab.Pane>,
        },
        {
            menuItem: "Documentations",
            render: () => <Tab.Pane>{documentation()}</Tab.Pane>,
        }
    ]

    return <Tab
        id={"AiScripterResultTabView"}
        panes={panes}
        className={"flexTab"}
        defaultActiveIndex={clampTabIndex(context.tabSession.aiScripterResultTab, panes)}
        onTabChange={(_, data) => {
            context.setSingleTabSession("aiScripterResultTab", parseInt(data.activeIndex.toString()))
        }}
        menu={{secondary: true, pointing: true, style: {flexShrink: 0, borderBottom: '1px solid #ddd'}}}
    />
}

